import React from 'react';
import { Form, Input, InputNumber, Select, DatePicker, Checkbox, Typography } from 'antd';
import type { FINCurr, ACCSetting } from '../../../services/exchangeRateApi';
import type { Mode } from '../hooks/useExchangeRateForm';
import dayjs from 'dayjs';

const { Text, Link } = Typography;

interface Props {
  mode: Mode;
  form: {
    f10e_01: string;
    f10e_02: string;
    f10e_12: number;
    f10e_11: number;
    f10e_13: number;
    f10e_14: number;
    exp_customs_rate: number;
    is_active: boolean;
    is_valid: boolean;
  };
  current: {
    f10e_91: string;
    user_name: string;
    cdate: string | null;
    uuser: string;
    udate: string | null;
    is_active?: boolean;
  } | null;
  rateUnitValue: number;
  currencies: FINCurr[];
  settings: ACCSetting | null;
  setField: (key: string, value: any) => void;
  onCurrencyChange: (currCode: string) => void;
}

const EditTab: React.FC<Props> = React.memo(({
  mode, form, current, rateUnitValue, currencies, settings,
  setField, onCurrencyChange,
}) => {
  const isEditing = mode !== 'browse';
  const labelCol = { style: { width: 130 } };

  return (
    <div className="er-edit-tab">
      <Form layout="horizontal" size="small" labelCol={labelCol} colon={false}>
        {/* Row 1: Currency + RateUnitValue */}
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <Form.Item label="幣別" required style={{ minWidth: 280 }}>
            <Select
              value={form.f10e_01 || undefined}
              onChange={onCurrencyChange}
              disabled={mode === 'edit' || mode === 'browse'}
              placeholder="選擇幣別"
              showSearch
              optionFilterProp="label"
              style={{ width: 160 }}
              options={currencies.map(c => ({
                value: c.curr_code,
                label: `${c.curr_code} - ${c.ccurr_name}`,
              }))}
            />
          </Form.Item>
          <Form.Item label="單位匯率比值">
            <InputNumber value={rateUnitValue} disabled precision={4} style={{ width: 120 }} />
          </Form.Item>
        </div>

        {/* Row 2: Date + Creator */}
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <Form.Item label="匯率日期" required style={{ minWidth: 280 }}>
            <DatePicker
              value={form.f10e_02 ? dayjs(form.f10e_02) : null}
              onChange={(d) => setField('f10e_02', d ? d.format('YYYY-MM-DD') : '')}
              disabled={mode === 'edit' || mode === 'browse'}
              format="YYYY-MM-DD"
              style={{ width: 160 }}
            />
          </Form.Item>
          <Form.Item label="建檔人員">
            <Text>{current?.f10e_91 || ''} {current?.user_name || ''}</Text>
          </Form.Item>
          <Form.Item label="建檔日期">
            <Text>{current?.cdate?.slice(0, 19) || ''}</Text>
          </Form.Item>
        </div>

        {/* Row 3: Checkboxes */}
        <div style={{ display: 'flex', gap: 24 }}>
          <Form.Item label="啟用">
            <Checkbox
              checked={form.is_active}
              onChange={e => setField('is_active', e.target.checked)}
              disabled={!isEditing || (mode === 'edit' && current?.is_active === true)}
            />
          </Form.Item>
          <Form.Item label="無效">
            <Checkbox
              checked={form.is_valid}
              onChange={e => setField('is_valid', e.target.checked)}
              disabled={!isEditing}
            />
          </Form.Item>
        </div>

        {/* Row 4: Rate fields */}
        <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
          系統默認計算: 當日預估匯率 = 前一天的即期匯率 × 0.98
        </Text>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <Form.Item label="預估匯率" required>
            <InputNumber
              value={form.f10e_12}
              onChange={v => setField('f10e_12', v ?? 0)}
              disabled={!isEditing}
              precision={4}
              min={0}
              style={{ width: 160 }}
            />
          </Form.Item>
          <Form.Item label="進口報關匯率" required>
            <InputNumber
              value={form.f10e_11}
              onChange={v => setField('f10e_11', v ?? 0)}
              disabled={!isEditing}
              precision={4}
              min={0}
              style={{ width: 160 }}
            />
          </Form.Item>
        </div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <Form.Item label="即期匯率(押匯)" required>
            <InputNumber
              value={form.f10e_13}
              onChange={v => setField('f10e_13', v ?? 0)}
              disabled={!isEditing}
              precision={4}
              min={0}
              style={{ width: 160 }}
            />
          </Form.Item>
          <Form.Item label="出口報關匯率" required>
            <InputNumber
              value={form.exp_customs_rate}
              onChange={v => setField('exp_customs_rate', v ?? 0)}
              disabled={!isEditing}
              precision={4}
              min={0}
              style={{ width: 160 }}
            />
          </Form.Item>
        </div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <Form.Item label="財務匯率">
            <InputNumber
              value={form.f10e_14}
              onChange={v => setField('f10e_14', v ?? 0)}
              disabled={!isEditing}
              precision={4}
              min={0}
              style={{ width: 160 }}
            />
          </Form.Item>
        </div>

        {/* Row 5: Formula hint */}
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12, color: 'steelblue' }}>
          1 建檔幣別 × 匯率 / 單位匯率比值 = ? 基礎幣別
        </Text>

        {/* Row 6: Reference URLs (BR-014) */}
        {settings && (
          <div style={{ marginTop: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>匯率參考網址</Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {settings.estimated_web && (
                <div>預估匯率: <Link href={settings.estimated_web} target="_blank">{settings.estimated_web}</Link></div>
              )}
              {settings.negotiation_web && (
                <div>即期匯率: <Link href={settings.negotiation_web} target="_blank">{settings.negotiation_web}</Link></div>
              )}
              {settings.customs_web && (
                <div>進出口報關匯率: <Link href={settings.customs_web} target="_blank">{settings.customs_web}</Link></div>
              )}
            </div>
          </div>
        )}
      </Form>
    </div>
  );
});

EditTab.displayName = 'EditTab';
export default EditTab;
